import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import { useAttentionItems, AttentionItem } from '../hooks/useAttentionItems';
import { useAndreiReminders, AndreiReminder } from '../hooks/useAndreiReminders';
import { useEmailPanel, PanelEmailData } from '../../mail/contexts/EmailPanelContext';
import { useAppData } from '../../../providers/AppDataProvider';
import { useInvoice } from '../../invoices/contexts/InvoiceContext';
import { useBills } from '../../bills/contexts/BillsContext';
import JobWorkspaceModal from '../../job-summary/JobWorkspaceModal';
import InvoiceViewModal from '../../invoices/components/InvoiceViewModal';
import { TerminalInvoiceModal } from '../../invoices/components/TerminalInvoiceModal';
import { useTerminalTheme } from '../../../hooks/useTerminalTheme';
import BillViewModal from '../../bills/components/BillViewModal';
import type { Job } from '../../job-summary/Interfaces';
import type { Invoice, Bill } from '../../../types/accounting';
import AttentionRow from '../components/AttentionRow';

/**
 * localStorage key for session-scoped dismissed ids, namespaced by local date
 * so dismissals clear naturally at day rollover. An invoice dismissed today
 * reappears tomorrow because it's still overdue.
 */
function dismissStorageKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `home_attention_dismissed_${y}${m}${dd}`;
}

function readDismissedIds(): Set<string> {
  try {
    const raw = localStorage.getItem(dismissStorageKey());
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function writeDismissedIds(ids: Set<string>): void {
  try {
    localStorage.setItem(dismissStorageKey(), JSON.stringify(Array.from(ids)));
  } catch {
    /* storage full or disabled — acceptable, dismissals just won't persist this reload */
  }
}

interface SectionProps {
  label: string;
  count: number;
  items: AttentionItem[];
  onItemClick: (item: AttentionItem) => void;
  onItemDismiss: (item: AttentionItem) => void;
  dismissHintFor: (item: AttentionItem) => string;
  /** Returns an onPushOut handler for the given item, or undefined if n/a. */
  pushOutHandlerFor: (item: AttentionItem) => ((item: AttentionItem, days: number) => void) | undefined;
}

/** Section header rendered as `── URGENT ──────────────── 3 ──`. */
const SectionHeader: React.FC<{ label: string; count: number }> = ({ label, count }) => (
  <div className="home-queue__section-header" role="heading" aria-level={3}>
    <span className="home-queue__section-lead">──</span>
    <span className="home-queue__section-label">{label}</span>
    <span className="home-queue__section-fill" aria-hidden="true" />
    <span className="home-queue__section-count">{count}</span>
    <span className="home-queue__section-trail">──</span>
  </div>
);

const Section: React.FC<SectionProps> = ({ label, count, items, onItemClick, onItemDismiss, dismissHintFor, pushOutHandlerFor }) => {
  if (items.length === 0) return null;
  return (
    <div className="home-queue__section">
      <SectionHeader label={label} count={count} />
      <ul className="home-queue__list">
        {items.map(item => (
          <li key={item.id}>
            <AttentionRow
              item={item}
              onClick={() => onItemClick(item)}
              onDismiss={onItemDismiss}
              dismissHint={dismissHintFor(item)}
              onPushOut={pushOutHandlerFor(item)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * AttentionQueue — the main dashboard surface. Priority-scored list across
 * all actionable signals grouped into URGENT / TODAY / WAITING ON YOU /
 * WAITING ON THEM. Click a row to open the underlying record **inline**
 * (same modals the rest of the app uses) rather than navigating away.
 *
 * Kinds and their open behavior:
 *   email       → EmailPanelContext.openEmailPanel('viewEmail', email)
 *   draft       → EmailPanelContext.setDraftMode('edit') + editDraft view
 *   job         → JobWorkspaceModal (re-looked-up by id to avoid stale data)
 *   invoice     → InvoiceViewModal  (re-looked-up from useInvoice().invoices)
 *   bill        → BillViewModal     (re-looked-up from useBills().bills)
 *
 * Stale-record safety: the AttentionItem payload carries a snapshot of the
 * record, but we resolve a fresh copy from the live context on click so the
 * modal never shows outdated data. If the record has vanished (e.g. voided
 * on another device between snapshot and click), we surface a toast and
 * skip the modal.
 */
/** A fired Andrei reminder row (kept separate from AttentionItem so it never
 *  touches the shared union / graphite feedModel). Reuses home-row styling. */
const ReminderRow: React.FC<{ item: AndreiReminder; onDismiss: (id: string) => void }> = ({ item, onDismiss }) => {
  const statusClass = `home-row__code home-row__code--${item.statusCode.toLowerCase()}`;
  return (
    <div className="home-row" role="listitem" aria-label={`Andrei reminder: ${item.title}. ${item.detail}`}>
      <span className={statusClass}>[{item.statusCode}]</span>
      <span className="home-row__title">{item.title}</span>
      {item.detail && <span className="home-row__sep">·</span>}
      {item.detail && <span className="home-row__subtitle">{item.detail}</span>}
      <button
        type="button"
        className="home-row__dismiss"
        onClick={(e) => { e.stopPropagation(); onDismiss(item.id); }}
        title="Mark reminder done"
        aria-label="Mark reminder done"
      >
        [done]
      </button>
    </div>
  );
};

const AttentionQueue: React.FC = () => {
  const attention = useAttentionItems();
  const reminders = useAndreiReminders();
  const { openEmailPanel, setEmailPanelView, setDraftMode } = useEmailPanel();
  const { jobs } = useAppData();
  const { invoices } = useInvoice();
  const { bills } = useBills();
  // Swap the invoice modal to the terminal-native one when this dashboard
  // is running under the terminal theme. JobWorkspaceModal is already
  // theme-branched internally; BillViewModal has no terminal version yet.
  const isTerminal = useTerminalTheme();
  const InvoiceModal = isTerminal ? TerminalInvoiceModal : InvoiceViewModal;

  // Inline modal state — exactly one modal can be open at a time.
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);

  // Session-scoped dismissed ids, namespaced per local day so they clear at
  // day rollover. An overdue invoice dismissed today still appears tomorrow
  // if it's still overdue — the user gets a fresh prompt.
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => readDismissedIds());

  const isDismissed = useCallback((id: string) => dismissedIds.has(id), [dismissedIds]);

  const addDismissed = useCallback((id: string) => {
    setDismissedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      writeDismissedIds(next);
      return next;
    });
  }, []);

  const handleDismiss = useCallback((item: AttentionItem) => {
    const p = item.payload;

    // Kind-aware dismiss semantics:
    //   unread_email → mark as read in Firestore (+ Graph if applicable).
    //                  Real action; won't re-surface tomorrow.
    //   everything else → session-scoped hide. Underlying record untouched.
    if (item.kind === 'unread_email' && p?.kind === 'email') {
      const email = p.email as PanelEmailData;
      const emailId = String((email as any)?.id || p.id || '');
      const isGraph = (email as any)?.source === 'graph';
      // Optimistic: immediately remove from the visible queue
      addDismissed(item.id);

      (async () => {
        try {
          // Firestore — single source of truth for the feed filter
          if (emailId) {
            await updateDoc(doc(db, 'emails', emailId), { dismissed: true, isRead: true });
          }
          // Graph — mirrors the read state back to Outlook for consistency
          if (isGraph && emailId) {
            try {
              await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/setGraphEmailReadState', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ emailId, isRead: true }),
              });
            } catch (graphErr) {
              // Non-fatal: Firestore already updated; Graph can drift and re-sync
              console.warn('[AttentionQueue] Graph mark-as-read failed (non-fatal):', graphErr);
            }
          }
        } catch (err) {
          console.error('[AttentionQueue] Mark-as-read failed:', err);
          toast.error("Couldn't mark email as read");
        }
      })();
      return;
    }

    // Everything else — just session-dismiss
    addDismissed(item.id);
  }, [addDismissed]);

  const dismissHintFor = useCallback((item: AttentionItem): string => {
    if (item.kind === 'unread_email') return 'Mark as read';
    if (item.kind === 'stale_draft') return 'Dismiss (draft kept)';
    return 'Dismiss for today';
  }, []);

  /**
   * Push a job's due date out by N days from TODAY (not from the existing
   * dueDate — the user asked for today-based rescheduling). If the job's
   * status is explicitly 'Overdue', flip it back to 'Open' in the same
   * write so the priority scorer stops flagging it as overdue.
   *
   * Matches the Firestore write shape used by ActiveCustomerList
   * (dueDate stored as full ISO string; readers parse via new Date()).
   */
  const handlePushOut = useCallback(async (item: AttentionItem, days: number) => {
    const p = item.payload;
    if (p?.kind !== 'job') return;
    const jobId = String(p.id || '');
    if (!jobId) {
      toast.error("Couldn't update job — missing id");
      return;
    }

    // Today's local date at midnight + N days. Using local midnight avoids
    // timezone flukes that could round the date forward/backward by a day.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(today.getTime() + days * 24 * 60 * 60 * 1000);

    const updates: Record<string, any> = {
      dueDate: target.toISOString(),
      lastEdited: new Date().toISOString(),
    };
    // If the current status is explicitly 'Overdue', reset to 'Open' so the
    // scorer's `status === 'Overdue'` branch stops flagging it. For other
    // statuses (Open/Pending/Active) the derived-overdue check resolves
    // naturally because dueDate is now in the future.
    const current = (p.job as any)?.status;
    if (current === 'Overdue') {
      updates.status = 'Open';
    }

    try {
      await updateDoc(doc(db, 'jobs', jobId), updates);
      const pretty = target.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
      const label = days === 7 ? '7 days' : days === 30 ? '30 days' : days === 365 ? '1 year' : `${days} days`;
      toast.success(`Pushed out ${label} — due ${pretty}`);
      // No need to dismiss locally: useAttentionItems recomputes on the next
      // AppDataProvider onSnapshot tick and this row disappears.
    } catch (err) {
      console.error('[AttentionQueue] Push-out failed:', err);
      toast.error("Couldn't update job due date");
    }
  }, []);

  /** Only overdue_job rows get the push-out menu. */
  const pushOutHandlerFor = useCallback((item: AttentionItem) => {
    if (item.kind === 'overdue_job') return handlePushOut;
    return undefined;
  }, [handlePushOut]);

  // Apply the dismissed filter before computing section counts so everything
  // stays consistent — the [4] in the section header matches the rows below.
  const filteredAttention = useMemo(() => {
    const f = (arr: AttentionItem[]) => arr.filter(i => !isDismissed(i.id));
    const urgent          = f(attention.urgent);
    const today           = f(attention.today);
    const waitingOnYou    = f(attention.waitingOnYou);
    const waitingOnThem   = f(attention.waitingOnThem);
    return {
      urgent, today, waitingOnYou, waitingOnThem,
      total: urgent.length + today.length + waitingOnYou.length + waitingOnThem.length,
    };
  }, [attention, isDismissed]);

  const { urgent, today, waitingOnYou, waitingOnThem, total } = filteredAttention;

  // Dismiss a fired Andrei reminder: mark the attention_items doc resolved so it
  // drops off (attention_items is client-writable via the catch-all rule).
  const dismissReminder = useCallback(async (id: string) => {
    try {
      await updateDoc(doc(db, 'attention_items', id), { status: 'resolved', updatedAt: new Date().toISOString() });
    } catch (err) {
      console.error('[AttentionQueue] reminder dismiss failed:', err);
      toast.error("Couldn't dismiss reminder");
    }
  }, []);

  const handleItemClick = (item: AttentionItem) => {
    const p = item.payload;
    if (!p) return;

    switch (p.kind) {
      case 'email': {
        const email = p.email as PanelEmailData;
        openEmailPanel('viewEmail', email);
        return;
      }
      case 'draft': {
        setDraftMode('edit', p.draft);
        setEmailPanelView('editDraft');
        return;
      }
      case 'job': {
        // Live lookup by id so the modal sees current status/dueDate/etc.
        // even if the attention item was scored a moment ago.
        const fresh = jobs.find(j => j.id === p.id) ?? (p.job as Job | undefined) ?? null;
        if (!fresh) {
          toast.info('That job is no longer available.');
          return;
        }
        setSelectedJob(fresh);
        return;
      }
      case 'invoice': {
        const fresh = invoices.find(i => i.id === p.id) ?? (p.invoice as Invoice | undefined) ?? null;
        if (!fresh) {
          toast.info('That invoice is no longer available.');
          return;
        }
        setSelectedInvoice(fresh);
        return;
      }
      case 'bill': {
        const fresh = bills.find(b => b.id === p.id) ?? (p.bill as Bill | undefined) ?? null;
        if (!fresh) {
          toast.info('That bill is no longer available.');
          return;
        }
        setSelectedBill(fresh);
        return;
      }
      default:
        return;
    }
  };

  const reminderSection = reminders.length > 0 && (
    <div className="home-queue__section">
      <SectionHeader label="ANDREI" count={reminders.length} />
      <ul className="home-queue__list">
        {reminders.map((r) => (
          <li key={r.id}>
            <ReminderRow item={r} onDismiss={dismissReminder} />
          </li>
        ))}
      </ul>
    </div>
  );

  // Empty state — replaces the whole section when nothing is vying for attention
  if (total === 0 && reminders.length === 0) {
    return (
      <div className="home-queue">
        <div className="home-queue__empty">
          <div className="home-queue__empty-line">─────────────────────────────────────────────────</div>
          <div className="home-queue__empty-msg">
            <span className="home-queue__empty-prompt">$</span>
            &nbsp;you're all caught up. nice.
          </div>
          <div className="home-queue__empty-line">─────────────────────────────────────────────────</div>
        </div>

        {/* Modals still need to render here to survive an empty-state → populated
            transition without losing any open modal. */}
        {renderModals(selectedJob, setSelectedJob, selectedInvoice, setSelectedInvoice, selectedBill, setSelectedBill, InvoiceModal)}
      </div>
    );
  }

  return (
    <div className="home-queue" role="region" aria-label="Attention queue">
      {reminderSection}
      <Section label="URGENT"          count={urgent.length}         items={urgent}         onItemClick={handleItemClick} onItemDismiss={handleDismiss} dismissHintFor={dismissHintFor} pushOutHandlerFor={pushOutHandlerFor} />
      <Section label="TODAY"           count={today.length}          items={today}          onItemClick={handleItemClick} onItemDismiss={handleDismiss} dismissHintFor={dismissHintFor} pushOutHandlerFor={pushOutHandlerFor} />
      <Section label="WAITING ON YOU"  count={waitingOnYou.length}   items={waitingOnYou}   onItemClick={handleItemClick} onItemDismiss={handleDismiss} dismissHintFor={dismissHintFor} pushOutHandlerFor={pushOutHandlerFor} />
      <Section label="WAITING ON THEM" count={waitingOnThem.length}  items={waitingOnThem}  onItemClick={handleItemClick} onItemDismiss={handleDismiss} dismissHintFor={dismissHintFor} pushOutHandlerFor={pushOutHandlerFor} />

      {renderModals(selectedJob, setSelectedJob, selectedInvoice, setSelectedInvoice, selectedBill, setSelectedBill, InvoiceModal)}
    </div>
  );
};

/**
 * Extracted so the empty and populated branches share identical modal
 * rendering without JSX duplication.
 */
function renderModals(
  selectedJob: Job | null,
  setSelectedJob: (j: Job | null) => void,
  selectedInvoice: Invoice | null,
  setSelectedInvoice: (i: Invoice | null) => void,
  selectedBill: Bill | null,
  setSelectedBill: (b: Bill | null) => void,
  // Theme-resolved invoice modal component — the AttentionQueue picks
  // TerminalInvoiceModal on terminal, InvoiceViewModal otherwise, then
  // passes the choice here so both render branches stay theme-correct.
  InvoiceModal: React.ComponentType<{
    show: boolean;
    invoice: Invoice | null;
    onHide: () => void;
  }>,
) {
  return (
    <>
      <JobWorkspaceModal
        show={!!selectedJob}
        job={selectedJob}
        onHide={() => setSelectedJob(null)}
      />
      <InvoiceModal
        show={!!selectedInvoice}
        invoice={selectedInvoice}
        onHide={() => setSelectedInvoice(null)}
      />
      <BillViewModal
        show={!!selectedBill}
        bill={selectedBill}
        onHide={() => setSelectedBill(null)}
      />
    </>
  );
}

export default AttentionQueue;
